// Marketing Website - Contact Form Handler
import { config } from './config.js';
import {
    getCurrentLanguage,
    applyTranslations,
    switchLanguage,
    t
} from './translations.js';
import { initTelemetry, trackPageView, trackFormSubmit, trackClick } from './telemetry/telemetryTracker.js';

console.log('CaninCloud Marketing Website');

// Initialize telemetry tracking
initTelemetry({
    apiEndpoint: `${config.apiUrl}/telemetry/track`,
    enableConsoleLogging: config.env === 'development',
    respectCookieConsent: true
});

// Contact Form Submission
document.addEventListener('DOMContentLoaded', () => {
    // Track page view
    trackPageView();

    // Track all anchor link clicks (section navigation like #features, #contact)
    document.querySelectorAll('a[href^="#"]').forEach(link => {
        link.addEventListener('click', () => {
            const href = link.getAttribute('href');
            if (href && href !== '#') {
                trackClick(link.id || href, link.textContent.trim(), {
                    section: href,
                    pagePath: window.location.pathname
                });
            }
        });
    });

    // Initialize translations
    const currentLang = getCurrentLanguage();
    applyTranslations(currentLang);

    // Initialize cookie banner
    initCookieBanner();

    // Initialize language switcher
    initLanguageSwitcher();

    // Update Play Store badge to match current language
    const playStoreBadge = document.getElementById('playStoreBadge');
    if (playStoreBadge) {
        const updatePlayStoreBadge = (lang) => {
            playStoreBadge.src = `/images/badges/google-play/play_store_${lang}.png`;
        };
        updatePlayStoreBadge(currentLang);
        window.addEventListener('languageChanged', (e) => updatePlayStoreBadge(e.detail.language));
    }

    // Set all app links to user app URL
    document.querySelectorAll('.app-link').forEach(link => {
        link.href = config.userAppUrl;
    });

    // Set specific login link
    const loginLink = document.getElementById('loginLink');
    if (loginLink) {
        loginLink.href = config.userAppUrl;
    }
    const registerLink = document.getElementById('registerlink');
    if(registerLink){
        registerLink.href = `${config.userAppUrl}/register.html`
    }

    // Notebook Slideshow
    initSlideshow();

    // Mobile Menu Toggle
    const mobileMenuToggle = document.getElementById('mobileMenuToggle');
    const navLinks = document.querySelector('.nav-links');

    if (mobileMenuToggle && navLinks) {
        mobileMenuToggle.addEventListener('click', () => {
            navLinks.classList.toggle('active');
            mobileMenuToggle.classList.toggle('active');
        });

        // Close menu when clicking on a link
        document.querySelectorAll('.nav-links a').forEach(link => {
            link.addEventListener('click', (e) => {
                navLinks.classList.remove('active');
                mobileMenuToggle.classList.remove('active');

                // Track anchor link clicks (for section navigation like #features, #contact)
                const href = link.getAttribute('href');
                if (href && href.startsWith('#')) {
                    trackClick(link.id || href, link.textContent.trim(), {
                        section: href,
                        pagePath: window.location.pathname
                    });
                }
            });
        });

        // Close menu when clicking outside
        document.addEventListener('click', (e) => {
            if (!navLinks.contains(e.target) && !mobileMenuToggle.contains(e.target)) {
                navLinks.classList.remove('active');
                mobileMenuToggle.classList.remove('active');
            }
        });
    }

    const contactForm = document.getElementById('contactForm');
    const submitButton = document.getElementById('contactSubmitButton');

    if (contactForm) {
        contactForm.addEventListener('submit', async (e) => {
            e.preventDefault();

            // Disable button during submission
            submitButton.disabled = true;
            submitButton.textContent = 'Wird gesendet...';

            const formData = new FormData(contactForm);

            // Split name into firstname and lastname
            const fullName = formData.get('name').trim();
            const nameParts = fullName.split(' ');
            const firstname = nameParts[0] || '';
            const lastname = nameParts.slice(1).join(' ') || '';

            const data = {
                firstname: firstname,
                lastname: lastname,
                mail: formData.get('email'),
                message: formData.get('message')
            };

            try {
                const response = await fetch(`${config.apiUrl}/contact`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                });

                if (response.ok) {
                    // Track successful form submission
                    trackFormSubmit('contactForm', {
                        success: true,
                        formType: 'contact'
                    });

                    // Show success message
                    showMessage('Nachricht erfolgreich gesendet! Wir melden uns bald bei Ihnen.', 'success');
                    contactForm.reset();
                } else {
                    throw new Error('Server error');
                }
            } catch (error) {
                console.error('Form submission error:', error);

                // Track failed form submission
                trackFormSubmit('contactForm', {
                    success: false,
                    formType: 'contact',
                    error: error.message
                });

                showMessage('Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.', 'error');
            } finally {
                submitButton.disabled = false;
                submitButton.textContent = 'Nachricht senden';
            }
        });
    }
});

function showMessage(message, type) {
    const contactContainer = document.querySelector('.contact-container');
    const existingMessage = document.querySelector('.message');

    // Remove existing message if any
    if (existingMessage) {
        existingMessage.remove();
    }

    // Create new message
    const messageDiv = document.createElement('div');
    messageDiv.className = `message message-${type}`;
    messageDiv.textContent = message;

    // Insert before form
    const form = document.getElementById('contactForm');
    contactContainer.insertBefore(messageDiv, form);

    // Auto-remove after 5 seconds
    setTimeout(() => {
        messageDiv.remove();
    }, 5000);
}

// Slideshow functionality
function initSlideshow() {
    const notebookSlides = document.querySelectorAll('.notebook-slideshow .slide');
    const mobileSlides = document.querySelectorAll('.mobile-slideshow .slide');
    const indicators = document.querySelectorAll('.indicator');
    const arrowLeft = document.querySelector('.slideshow-arrow-left');
    const arrowRight = document.querySelector('.slideshow-arrow-right');

    if (!notebookSlides.length || !indicators.length) {
        return; // Slideshow not present on this page
    }

    const slideCount = notebookSlides.length;
    let currentSlide = 0;
    let autoPlayInterval = null;

    // Update active slide and indicator
    function showSlide(index) {
        notebookSlides.forEach(slide => slide.classList.remove('active'));
        mobileSlides.forEach(slide => slide.classList.remove('active'));
        indicators.forEach(indicator => indicator.classList.remove('active'));

        notebookSlides[index].classList.add('active');
        if (mobileSlides[index]) mobileSlides[index].classList.add('active');
        indicators[index].classList.add('active');

        currentSlide = index;
    }

    // Go to next slide
    function nextSlide() {
        const next = (currentSlide + 1) % slideCount;
        showSlide(next);
    }

    // Go to previous slide
    function prevSlide() {
        const prev = (currentSlide - 1 + slideCount) % slideCount;
        showSlide(prev);
    }

    // Start auto-play
    function startAutoPlay() {
        stopAutoPlay(); // Clear any existing interval
        autoPlayInterval = setInterval(nextSlide, 5000); // Change slide every 5 seconds
    }

    // Stop auto-play
    function stopAutoPlay() {
        if (autoPlayInterval) {
            clearInterval(autoPlayInterval);
            autoPlayInterval = null;
        }
    }

    // Arrow click handlers
    if (arrowLeft) {
        arrowLeft.addEventListener('click', () => {
            prevSlide();
            stopAutoPlay();
            startAutoPlay(); // Restart auto-play after manual navigation
        });
    }

    if (arrowRight) {
        arrowRight.addEventListener('click', () => {
            nextSlide();
            stopAutoPlay();
            startAutoPlay(); // Restart auto-play after manual navigation
        });
    }

    // Indicator click handlers
    indicators.forEach((indicator, index) => {
        indicator.addEventListener('click', () => {
            showSlide(index);
            stopAutoPlay();
            startAutoPlay(); // Restart auto-play after manual navigation
        });
    });

    // Pause auto-play when user hovers over slideshow
    const slideshowContainer = document.querySelector('.notebook-slideshow');
    if (slideshowContainer) {
        slideshowContainer.addEventListener('mouseenter', stopAutoPlay);
        slideshowContainer.addEventListener('mouseleave', startAutoPlay);
    }

    // Start auto-play on page load
    startAutoPlay();
}

// Cookie Banner functionality
function initCookieBanner() {
    // Check if user has already accepted cookies
    const cookieConsent = getCookie('cookie_consent');

    if (!cookieConsent) {
        // Show banner after a short delay
        setTimeout(() => {
            const banner = document.getElementById('cookie-banner');
            if (banner) {
                banner.classList.add('show');
            }
        }, 1000);
    }

    // Accept button handler
    const acceptBtn = document.getElementById('cookie-accept');
    if (acceptBtn) {
        acceptBtn.addEventListener('click', () => {
            setCookie('cookie_consent', 'accepted', 365);
            const banner = document.getElementById('cookie-banner');
            if (banner) {
                banner.classList.remove('show');
            }

            // Reinitialize telemetry now that consent is given
            initTelemetry({
                apiEndpoint: `${config.apiUrl}/telemetry/track`,
                enableConsoleLogging: config.env === 'development',
                respectCookieConsent: true
            });

            // Track the current page view
            trackPageView();
        });
    }
}

// Language Switcher functionality
function initLanguageSwitcher() {
    const switcherBtn = document.querySelector('.language-switcher-btn');
    const dropdown = document.querySelector('.language-dropdown');
    const languageOptions = document.querySelectorAll('.language-option');

    if (!switcherBtn || !dropdown) return;

    // Update current language display
    const currentLang = getCurrentLanguage();
    const langText = switcherBtn.querySelector('.lang-text');
    if (langText) {
        langText.textContent = currentLang.toUpperCase();
    }

    // Update active state in dropdown
    updateActiveLanguage(currentLang);

    // Toggle dropdown
    switcherBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        dropdown.classList.toggle('show');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', () => {
        dropdown.classList.remove('show');
    });

    // Language option click handlers
    languageOptions.forEach(option => {
        option.addEventListener('click', () => {
            const lang = option.getAttribute('data-lang');
            switchLanguage(lang);

            // Update display
            if (langText) {
                langText.textContent = lang.toUpperCase();
            }
            updateActiveLanguage(lang);

            dropdown.classList.remove('show');
        });
    });
}

// Update active language in dropdown
function updateActiveLanguage(lang) {
    document.querySelectorAll('.language-option').forEach(option => {
        if (option.getAttribute('data-lang') === lang) {
            option.classList.add('active');
            const checkIcon = option.querySelector('.material-symbols-outlined');
            if (checkIcon) {
                checkIcon.style.display = 'block';
            }
        } else {
            option.classList.remove('active');
            const checkIcon = option.querySelector('.material-symbols-outlined');
            if (checkIcon) {
                checkIcon.style.display = 'none';
            }
        }
    });
}

// Cookie helper functions
function getCookie(name) {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [cookieName, cookieValue] = cookie.trim().split('=');
        if (cookieName === name) {
            return cookieValue;
        }
    }
    return null;
}

function setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}
